// CommunityDetail.tsx
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  HStack,
  VStack,
  Avatar,
  Badge,
  IconButton,
  useToast,
  Spinner,
  Center,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useBreakpointValue,
  Textarea,
  useDisclosure,
  Image,
  Grid
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { ArrowBackIcon } from '@chakra-ui/icons'
import { FiEdit3, FiTrash2, FiMoreVertical, FiHeart, FiMessageCircle, FiBookmark } from 'react-icons/fi'
import DefaultLayout from '../components/layouts/DefaultLayout'
import ImageViewerModal from '../components/community/ImageViewerModal'
import NicknameModal from '../components/community/NicknameModal'
import { 
  getCommunityPost, 
  getCommunityComments, 
  deleteCommunityPost, 
  createCommunityComment, 
  deleteCommunityComment,
  likeCommunityPost,
  dislikeCommunityPost,
  getUserPostLikeStatus,
  getUserCommentLikeStatus,
  likeCommunityComment,
  dislikeCommunityComment
} from '../services/apiService'

interface CommunityPost {
  id: number
  user_id: number
  title: string
  content: string
  view_count: number
  like_count: number
  dislike_count: number
  comment_count: number
  status: string
  category?: 'FREE' | 'PROFIT'
  image_urls?: string[]
  created_at: string
  updated_at: string
  user: {
    id: number
    username: string
    nickname?: string
    profile_image_url?: string
    grade?: string
    partner_count?: string | null
  }
}

interface CommunityComment {
  id: number
  community_id: number
  user_id: number
  content: string
  parent_id?: number
  depth: number
  like_count: number
  dislike_count: number
  status: string
  created_at: string
  updated_at: string
  user: {
    id: number
    username: string
    nickname?: string
    profile_image_url?: string
    grade?: string
    partner_count?: string | null
  }
}

const CommunityDetail = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [auth] = useRecoilState(authState)
  const toast = useToast()
  
  const [post, setPost] = useState<CommunityPost | null>(null)
  const [comments, setComments] = useState<CommunityComment[]>([])
  const [loading, setLoading] = useState(true)
  const [commentContent, setCommentContent] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [isLiked, setIsLiked] = useState(false)
  const [isDisliked, setIsDisliked] = useState(false)
  const [likedComments, setLikedComments] = useState<Set<number>>(new Set())
  const [dislikedComments, setDislikedComments] = useState<Set<number>>(new Set())
  const [replyingTo, setReplyingTo] = useState<number | null>(null)
  const [replyContent, setReplyContent] = useState('')
  const [imageViewerOpen, setImageViewerOpen] = useState(false)
  const [viewerImages, setViewerImages] = useState<string[]>([])
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0)
  const [nicknameModalOpen, setNicknameModalOpen] = useState(false)
  
  const isMobile = useBreakpointValue({ base: true, md: false })

  // 게시글 상세 조회
  const fetchPost = async () => {
    try {
      setLoading(true)
      const response = await getCommunityPost(Number(id))
      const postData = response.data.data
      setPost(postData)

      // 로그인한 사용자의 좋아요/싫어요 상태 확인
      if (auth.id) {
        try {
          const likeStatus = await getUserPostLikeStatus(postData.id)
          setIsLiked(likeStatus.isLiked)
          setIsDisliked(likeStatus.isDisliked)
        } catch (error) {
          console.error('Post like status error:', error)
          setIsLiked(false)
          setIsDisliked(false)
        }
      }
    } catch (error) {
      toast({
        title: '오류',
        description: '게시글을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  // 댓글 목록 조회
  const fetchComments = async () => {
    try {
      const response = await getCommunityComments(Number(id))
      const commentsData = response.data.data
      setComments(commentsData)

      // 로그인한 사용자의 댓글 좋아요/싫어요 상태 확인
      if (auth.id) {
        const commentLikeStatuses = await Promise.all(
          commentsData.map(async (comment: CommunityComment) => {
            try {
              const status = await getUserCommentLikeStatus(comment.id)
              return { commentId: comment.id, ...status }
            } catch (error) {
              console.error(`Comment ${comment.id} like status error:`, error)
              return { commentId: comment.id, isLiked: false, isDisliked: false }
            }
          })
        )

        // 댓글 좋아요/싫어요 상태를 Set에 저장
        const newLikedComments = new Set<number>()
        const newDislikedComments = new Set<number>()

        commentLikeStatuses.forEach(({ commentId, isLiked, isDisliked }) => {
          if (isLiked) newLikedComments.add(commentId)
          if (isDisliked) newDislikedComments.add(commentId)
        })

        setLikedComments(newLikedComments)
        setDislikedComments(newDislikedComments)
      }
    } catch (error) {
      console.error('댓글 조회 실패:', error)
    }
  }

  useEffect(() => {
    if (id) {
      fetchPost()
      fetchComments()
    }
  }, [id])

  const handleEditPost = () => {
    if (!post || post.user_id !== auth.id) {
      toast({
        title: '권한 없음',
        description: '본인의 글만 수정할 수 있습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }
    navigate(`/community/${post.id}/edit`)
  }

  const handleDeletePost = async () => {
    if (!post || post.user_id !== auth.id) {
      toast({
        title: '권한 없음',
        description: '본인의 글만 삭제할 수 있습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }
    
    if (window.confirm('정말로 이 글을 삭제하시겠습니까?')) {
      try {
        await deleteCommunityPost(post.id)
        toast({
          title: '삭제 완료',
          description: '게시글이 삭제되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        navigate('/community')
      } catch (error) {
        toast({
          title: '오류',
          description: '게시글 삭제에 실패했습니다.',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    }
  }

  const handleSubmitComment = async () => {
    if (!auth.id) {
      toast({
        title: '로그인 필요',
        description: '댓글을 작성하려면 로그인이 필요합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    // 닉네임이 없으면 닉네임 설정 모달 표시
    if (!auth.nickname) {
      setNicknameModalOpen(true)
      return
    }

    if (!commentContent.trim()) {
      toast({
        title: '댓글을 입력해주세요',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      setSubmittingComment(true)
      await createCommunityComment(Number(id), { content: commentContent })
      toast({
        title: '댓글 작성 완료',
        description: '댓글이 작성되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
      setCommentContent('')
      
      // 댓글 카운트 증가
      setPost(prev => prev ? {
        ...prev,
        comment_count: prev.comment_count + 1
      } : null)
      
      fetchComments()
    } catch (error) {
      toast({
        title: '오류',
        description: '댓글 작성에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleDeleteComment = async (comment: CommunityComment) => {
    if (comment.user_id !== auth.id) {
      toast({
        title: '권한 없음',
        description: '본인의 댓글만 삭제할 수 있습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }
    
    if (window.confirm('정말로 이 댓글을 삭제하시겠습니까?')) {
      try {
        await deleteCommunityComment(comment.id)
        toast({
          title: '삭제 완료',
          description: '댓글이 삭제되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        
        // 댓글 카운트 감소
        setPost(prev => prev ? {
          ...prev,
          comment_count: Math.max(0, prev.comment_count - 1)
        } : null)
        
        fetchComments()
      } catch (error) {
        toast({
          title: '오류',
          description: '댓글 삭제에 실패했습니다.',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return '방금 전'
    if (diffInMinutes < 60) return `${diffInMinutes}분 전`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}시간 전`
    return `${Math.floor(diffInMinutes / 1440)}일 전`
  }

  const handleLikePost = async () => {
    if (!auth.id) {
      toast({
        title: '로그인 필요',
        description: '좋아요를 누르려면 로그인이 필요합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      const response = await likeCommunityPost(Number(id))
      console.log('Like response:', response) // 디버깅용
      console.log('Like response.data:', response.data) // 디버깅용
      console.log('Like response.data.data:', response.data.data) // 디버깅용
      
      const action = (response.data as any)?.data?.action || (response.data as any)?.action
      console.log('Action:', action) // 디버깅용
      
      // 백엔드 응답에 따라 상태 업데이트
      if (action === 'like') {
        // 새로운 좋아요
        setIsLiked(true)
        setIsDisliked(false)
        setPost(prev => prev ? {
          ...prev,
          like_count: prev.like_count + 1
        } : null)
      } else if (action === 'like_cancelled') {
        // 좋아요 취소
        setIsLiked(false)
        setPost(prev => prev ? {
          ...prev,
          like_count: Math.max(0, prev.like_count - 1)
        } : null)
      } else if (action === 'dislike_to_like') {
        // 싫어요에서 좋아요로 변경
        setIsLiked(true)
        setIsDisliked(false)
        setPost(prev => prev ? {
          ...prev,
          like_count: prev.like_count + 1,
          dislike_count: Math.max(0, prev.dislike_count - 1)
        } : null)
      } else {
        // action이 없거나 예상하지 못한 경우, 게시글을 다시 로드
        console.log('예상하지 못한 action:', action, '게시글 다시 로드')
        fetchPost()
      }
    } catch (error) {
      console.error('좋아요 처리 실패:', error)
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류'
      toast({
        title: '오류',
        description: `좋아요 처리에 실패했습니다: ${errorMessage}`,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  const handleDislikePost = async () => {
    if (!auth.id) {
      toast({
        title: '로그인 필요',
        description: '싫어요를 누르려면 로그인이 필요합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      const response = await dislikeCommunityPost(Number(id))
      console.log('Dislike response:', response) // 디버깅용
      console.log('Dislike response.data:', response.data) // 디버깅용
      console.log('Dislike response.data.data:', response.data.data) // 디버깅용
      
      const action = (response.data as any)?.data?.action || (response.data as any)?.action
      console.log('Dislike Action:', action) // 디버깅용
      
      // 백엔드 응답에 따라 상태 업데이트
      if (action === 'dislike') {
        // 새로운 싫어요
        setIsDisliked(true)
        setIsLiked(false)
        setPost(prev => prev ? {
          ...prev,
          dislike_count: prev.dislike_count + 1
        } : null)
      } else if (action === 'dislike_cancelled') {
        // 싫어요 취소
        setIsDisliked(false)
        setPost(prev => prev ? {
          ...prev,
          dislike_count: Math.max(0, prev.dislike_count - 1)
        } : null)
      } else if (action === 'like_to_dislike') {
        // 좋아요에서 싫어요로 변경
        setIsDisliked(true)
        setIsLiked(false)
        setPost(prev => prev ? {
          ...prev,
          dislike_count: prev.dislike_count + 1,
          like_count: Math.max(0, prev.like_count - 1)
        } : null)
      } else {
        // action이 없거나 예상하지 못한 경우, 게시글을 다시 로드
        console.log('예상하지 못한 dislike action:', action, '게시글 다시 로드')
        fetchPost()
      }
    } catch (error) {
      console.error('싫어요 처리 실패:', error)
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류'
      toast({
        title: '오류',
        description: `싫어요 처리에 실패했습니다: ${errorMessage}`,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  const handleLikeComment = async (commentId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    
    if (!auth.id) {
      toast({
        title: '로그인 필요',
        description: '좋아요를 누르려면 로그인이 필요합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      const response = await likeCommunityComment(commentId)
      console.log('Comment like response:', response) // 디버깅용
      console.log('Comment like response.data:', response.data) // 디버깅용
      console.log('Comment like response.data.data:', response.data.data) // 디버깅용
      
      const action = (response.data as any)?.data?.action || (response.data as any)?.action
      console.log('Comment Action:', action) // 디버깅용
      
      // 백엔드 응답에 따라 상태 업데이트
      if (action === 'like') {
        // 새로운 좋아요
        setLikedComments(prev => new Set(prev).add(commentId))
        setDislikedComments(prev => {
          const newSet = new Set(prev)
          newSet.delete(commentId)
          return newSet
        })
        
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, like_count: comment.like_count + 1 }
            : comment
        ))
      } else if (action === 'like_cancelled') {
        // 좋아요 취소
        setLikedComments(prev => {
          const newSet = new Set(prev)
          newSet.delete(commentId)
          return newSet
        })
        
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, like_count: Math.max(0, comment.like_count - 1) }
            : comment
        ))
      } else if (action === 'dislike_to_like') {
        // 싫어요에서 좋아요로 변경
        setLikedComments(prev => new Set(prev).add(commentId))
        setDislikedComments(prev => {
          const newSet = new Set(prev)
          newSet.delete(commentId)
          return newSet
        })
        
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { 
                ...comment, 
                like_count: comment.like_count + 1,
                dislike_count: Math.max(0, comment.dislike_count - 1)
              }
            : comment
        ))
      } else {
        // action이 없거나 예상하지 못한 경우, 댓글 목록을 다시 로드
        console.log('예상하지 못한 comment like action:', action, '댓글 목록 다시 로드')
        fetchComments()
      }
    } catch (error) {
      console.error('댓글 좋아요 처리 실패:', error)
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류'
      toast({
        title: '오류',
        description: `댓글 좋아요 처리에 실패했습니다: ${errorMessage}`,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  const handleDislikeComment = async (commentId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    
    if (!auth.id) {
      toast({
        title: '로그인 필요',
        description: '싫어요를 누르려면 로그인이 필요합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      const response = await dislikeCommunityComment(commentId)
      console.log('Comment dislike response:', response) // 디버깅용
      console.log('Comment dislike response.data:', response.data) // 디버깅용
      console.log('Comment dislike response.data.data:', response.data.data) // 디버깅용
      
      const action = (response.data as any)?.data?.action || (response.data as any)?.action
      console.log('Comment Dislike Action:', action) // 디버깅용
      
      // 백엔드 응답에 따라 상태 업데이트
      if (action === 'dislike') {
        // 새로운 싫어요
        setDislikedComments(prev => new Set(prev).add(commentId))
        setLikedComments(prev => {
          const newSet = new Set(prev)
          newSet.delete(commentId)
          return newSet
        })
        
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, dislike_count: comment.dislike_count + 1 }
            : comment
        ))
      } else if (action === 'dislike_cancelled') {
        // 싫어요 취소
        setDislikedComments(prev => {
          const newSet = new Set(prev)
          newSet.delete(commentId)
          return newSet
        })
        
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, dislike_count: Math.max(0, comment.dislike_count - 1) }
            : comment
        ))
      } else if (action === 'like_to_dislike') {
        // 좋아요에서 싫어요로 변경
        setDislikedComments(prev => new Set(prev).add(commentId))
        setLikedComments(prev => {
          const newSet = new Set(prev)
          newSet.delete(commentId)
          return newSet
        })
        
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { 
                ...comment, 
                dislike_count: comment.dislike_count + 1,
                like_count: Math.max(0, comment.like_count - 1)
              }
            : comment
        ))
      } else {
        // action이 없거나 예상하지 못한 경우, 댓글 목록을 다시 로드
        console.log('예상하지 못한 comment dislike action:', action, '댓글 목록 다시 로드')
        fetchComments()
      }
    } catch (error) {
      console.error('댓글 싫어요 처리 실패:', error)
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류'
      toast({
        title: '오류',
        description: `댓글 싫어요 처리에 실패했습니다: ${errorMessage}`,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  const handleReplyToComment = (commentId: number) => {
    setReplyingTo(commentId)
    setReplyContent('')
  }

  const handleSubmitReply = async (parentCommentId: number) => {
    if (!auth.id) {
      toast({
        title: '로그인 필요',
        description: '댓글을 작성하려면 로그인이 필요합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    // 닉네임이 없으면 닉네임 설정 모달 표시
    if (!auth.nickname) {
      setNicknameModalOpen(true)
      return
    }

    if (!replyContent.trim()) {
      toast({
        title: '댓글을 입력해주세요',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      await createCommunityComment(Number(id), { 
        content: replyContent, 
        parent_id: parentCommentId 
      })
      
      toast({
        title: '대댓글 작성 완료',
        description: '대댓글이 작성되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
      
      setReplyContent('')
      setReplyingTo(null)
      
      // 댓글 카운트 증가
      setPost(prev => prev ? {
        ...prev,
        comment_count: prev.comment_count + 1
      } : null)
      
      fetchComments()
    } catch (error) {
      toast({
        title: '오류',
        description: '대댓글 작성에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  if (loading) {
    return (
      <DefaultLayout>
        <Center py={20}>
          <Spinner size="xl" />
        </Center>
      </DefaultLayout>
    )
  }

  if (!post) {
    return (
      <DefaultLayout>
        <Center py={20}>
          <VStack spacing={4}>
            <Text color="gray.500" fontSize="lg">
              게시글을 찾을 수 없습니다.
            </Text>
            <Button onClick={() => navigate('/community')}>
              목록으로 돌아가기
            </Button>
          </VStack>
        </Center>
      </DefaultLayout>
    )
  }

  return (
    <DefaultLayout>
      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        <HStack mb={6}>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            onClick={() => navigate('/community')}
          >
            뒤로가기
          </Button>
        </HStack>

        {/* 게시글 내용 */}
        <Box
          bg="white"
          borderRadius="lg"
          border="1px"
          borderColor="gray.200"
          p={6}
          mb={6}
        >
          <Flex justify="space-between" align="start" mb={4}>
            <HStack spacing={3}>
              <Avatar
                size="md"
                name={post.user.nickname || post.user.username}
                src={post.user.profile_image_url || undefined}
              />
              <VStack align="start" spacing={0}>
                <HStack spacing={2} align="center">
                  <Text fontSize="md" fontWeight="medium">
                    {post.user.nickname || post.user.username}
                  </Text>
                  {/* 사용자 등급 뱃지 */}
                  {post.user.grade && (
                    <Badge
                      colorScheme={
                        post.user.grade === 'SECRET' ? 'purple' :
                        post.user.grade === 'VIP' ? 'yellow' :
                        post.user.grade === 'BASIC' ? 'blue' :
                        'gray'
                      }
                      variant="solid"
                      fontSize="xs"
                      fontWeight="bold"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                    >
                      {post.user.grade}
                    </Badge>
                  )}
                  {/* 파트너 수 뱃지 */}
                  {post.user.partner_count && (
                    <Badge
                      colorScheme="green"
                      variant="outline"
                      fontSize="xs"
                      fontWeight="semibold"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                    >
                      파트너 {post.user.partner_count}
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="sm" color="gray.500">
                  {formatTimeAgo(post.created_at)}
                </Text>
              </VStack>
            </HStack>
            
            <HStack spacing={2}>
              <Badge 
                colorScheme={post.category === 'PROFIT' ? 'green' : 'blue'} 
                variant="solid"
                fontSize="xs"
                fontWeight="semibold"
              >
                {post.category === 'PROFIT' ? '💰 수익인증' : '💬 자유게시판'}
              </Badge>
              {auth.id === post.user_id && (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<FiMoreVertical />}
                    variant="ghost"
                    size="sm"
                  />
                  <MenuList>
                    <MenuItem
                      icon={<FiEdit3 />}
                      onClick={handleEditPost}
                    >
                      수정
                    </MenuItem>
                    <MenuItem
                      icon={<FiTrash2 />}
                      color="red.500"
                      onClick={handleDeletePost}
                    >
                      삭제
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
            </HStack>
          </Flex>

          <VStack align="start" spacing={4} mb={6}>
            <Heading as="h1" size="lg">
              {post.title}
            </Heading>
            
            {/* 이미지 표시 */}
            {post.image_urls && post.image_urls.length > 0 && (
              <Box w="100%" mb={4}>
                <Grid 
                  templateColumns={
                    post.image_urls.length === 1 
                      ? "1fr" 
                      : post.image_urls.length === 2 
                      ? "1fr 1fr" 
                      : "repeat(3, 1fr)"
                  } 
                  gap={4}
                  maxW="600px"
                >
                  {post.image_urls.map((imageUrl, index) => (
                    <Image
                      key={index}
                      src={imageUrl}
                      alt={`게시글 이미지 ${index + 1}`}
                      borderRadius="lg"
                      objectFit="cover"
                      h={post.image_urls!.length === 1 ? "300px" : "200px"}
                      w="100%"
                      border="1px solid"
                      borderColor="gray.200"
                      boxShadow="0 2px 8px rgba(0,0,0,0.1)"
                      cursor="pointer"
                      onClick={() => {
                        setViewerImages(post.image_urls!)
                        setViewerInitialIndex(index)
                        setImageViewerOpen(true)
                      }}
                      _hover={{ opacity: 0.8 }}
                    />
                  ))}
                </Grid>
              </Box>
            )}
            
            <Box
              dangerouslySetInnerHTML={{ __html: post.content }}
              w="100%"
              sx={{
                '& p': { marginBottom: '1rem' },
                '& h1, & h2, & h3': { marginBottom: '0.5rem' },
                '& ul, & ol': { marginLeft: '1.5rem', marginBottom: '1rem' }
              }}
            />
          </VStack>

          <HStack spacing={6}>
            <HStack 
              spacing={1} 
              cursor="pointer" 
              _hover={{ opacity: 0.7 }}
              onClick={handleLikePost}
            >
              <FiHeart 
                size={20} 
                color={isLiked ? "#e53e3e" : "gray"} 
                fill={isLiked ? "#e53e3e" : "none"}
              />
              <Text fontSize="md" color={isLiked ? "red.500" : "gray.600"}>
                {post.like_count}
              </Text>
            </HStack>
            <HStack 
              spacing={1} 
              cursor="pointer" 
              _hover={{ opacity: 0.7 }}
              onClick={handleDislikePost}
            >
              <Text fontSize="md" color={isDisliked ? "red.500" : "gray.600"}>
                👎
              </Text>
              <Text fontSize="md" color={isDisliked ? "red.500" : "gray.600"}>
                {post.dislike_count}
              </Text>
            </HStack>
            <HStack spacing={1}>
              <FiMessageCircle size={20} color="gray" />
              <Text fontSize="md" color="gray.600">
                {post.comment_count}
              </Text>
            </HStack>
          </HStack>
        </Box>

        {/* 댓글 입력 */}
        {auth.id && (
          <Box
            bg="white"
            borderRadius="lg"
            border="1px"
            borderColor="gray.200"
            p={4}
            mb={6}
          >
            <HStack spacing={3} mb={3}>
              <Avatar
                size="sm"
                name={auth.nickname || auth.username}
                src={auth.profileImageUrl || undefined}
              />
              <Text fontSize="sm" color="gray.600">
                {auth.nickname || auth.username}님의 생각을 공유해보세요.
              </Text>
            </HStack>
            
            <VStack spacing={3}>
              <Textarea
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
                placeholder="댓글을 입력하세요..."
                rows={3}
                resize="none"
              />
              <Button
                bg="linear-gradient(135deg, #48bb78 0%, #38a169 100%)"
                color="white"
                size="sm"
                onClick={handleSubmitComment}
                isLoading={submittingComment}
                loadingText="작성 중..."
                alignSelf="flex-end"
                borderRadius="lg"
                px={6}
                py={3}
                fontSize="sm"
                fontWeight="semibold"
                boxShadow="0 4px 15px rgba(72, 187, 120, 0.3)"
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(72, 187, 120, 0.4)',
                  bg: 'linear-gradient(135deg, #38a169 0%, #2f855a 100%)'
                }}
                _active={{
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 10px rgba(72, 187, 120, 0.3)'
                }}
                transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                _loading={{
                  bg: 'linear-gradient(135deg, #68d391 0%, #48bb78 100%)',
                  cursor: 'not-allowed'
                }}
              >
                💬 이 댓글로 남길게요
              </Button>
            </VStack>
          </Box>
        )}

        {/* 댓글 목록 */}
        <Box
          bg="white"
          borderRadius="lg"
          border="1px"
          borderColor="gray.200"
          p={4}
        >
          <HStack justify="space-between" mb={4}>
            <Text fontSize="lg" fontWeight="bold">
              댓글 {comments.length}
            </Text>
            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.500">
                등록순
              </Text>
              <Text fontSize="sm" color="gray.400">
                ↕
              </Text>
            </HStack>
          </HStack>

          <VStack spacing={4} align="stretch">
            {comments.map((comment) => (
              <Box key={comment.id} pl={comment.depth * 4}>
                <Flex justify="space-between" align="start" mb={2}>
                  <HStack spacing={3}>
                    <Avatar
                      size="sm"
                      name={comment.user.nickname || comment.user.username}
                      src={comment.user.profile_image_url || undefined}
                    />
                    <VStack align="start" spacing={0}>
                      <HStack spacing={2} align="center">
                        <Text fontSize="sm" fontWeight="medium">
                          {comment.user.nickname || comment.user.username}
                        </Text>
                        {/* 사용자 등급 뱃지 */}
                        {comment.user.grade && (
                          <Badge
                            colorScheme={
                              comment.user.grade === 'SECRET' ? 'purple' :
                              comment.user.grade === 'VIP' ? 'yellow' :
                              comment.user.grade === 'BASIC' ? 'blue' :
                              'gray'
                            }
                            variant="solid"
                            fontSize="xs"
                            fontWeight="bold"
                            px={1.5}
                            py={0.5}
                            borderRadius="sm"
                          >
                            {comment.user.grade}
                          </Badge>
                        )}
                        {/* 파트너 수 뱃지 */}
                        {comment.user.partner_count && (
                          <Badge
                            colorScheme="green"
                            variant="outline"
                            fontSize="xs"
                            fontWeight="semibold"
                            px={1.5}
                            py={0.5}
                            borderRadius="sm"
                          >
                            파트너 {comment.user.partner_count}
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        {formatTimeAgo(comment.created_at)}
                      </Text>
                    </VStack>
                  </HStack>
                  
                  {auth.id === comment.user_id && (
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        variant="ghost"
                        size="xs"
                      />
                      <MenuList>
                        <MenuItem
                          icon={<FiTrash2 />}
                          color="red.500"
                          onClick={() => handleDeleteComment(comment)}
                        >
                          삭제
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </Flex>
                
                <Text fontSize="sm" mb={2} pl={10}>
                  {comment.content}
                </Text>
                
                <HStack spacing={4} pl={10}>
                  <HStack 
                    spacing={1} 
                    cursor="pointer" 
                    _hover={{ opacity: 0.7 }}
                    onClick={(e) => handleLikeComment(comment.id, e)}
                  >
                    <FiHeart 
                      size={14} 
                      color={likedComments.has(comment.id) ? "#e53e3e" : "gray"} 
                      fill={likedComments.has(comment.id) ? "#e53e3e" : "none"}
                    />
                    <Text fontSize="xs" color={likedComments.has(comment.id) ? "red.500" : "gray.600"}>
                      {comment.like_count}
                    </Text>
                  </HStack>
                  <HStack 
                    spacing={1} 
                    cursor="pointer" 
                    _hover={{ opacity: 0.7 }}
                    onClick={(e) => handleDislikeComment(comment.id, e)}
                  >
                    <Text fontSize="xs" color={dislikedComments.has(comment.id) ? "red.500" : "gray.600"}>
                      👎
                    </Text>
                    <Text fontSize="xs" color={dislikedComments.has(comment.id) ? "red.500" : "gray.600"}>
                      {comment.dislike_count}
                    </Text>
                  </HStack>
                  <Button
                    size="xs"
                    variant="ghost"
                    fontSize="xs"
                    color="gray.500"
                    onClick={() => handleReplyToComment(comment.id)}
                  >
                    댓글 남기기
                  </Button>
                </HStack>

                {/* 대댓글 입력 */}
                {replyingTo === comment.id && (
                  <Box pl={10} mt={3} p={3} bg="gray.50" borderRadius="md">
                    <VStack spacing={3}>
                      <Textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="대댓글을 입력하세요..."
                        rows={2}
                        resize="none"
                        size="sm"
                      />
                      <HStack spacing={2} alignSelf="flex-end">
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => {
                            setReplyingTo(null)
                            setReplyContent('')
                          }}
                        >
                          취소
                        </Button>
                        <Button
                          size="xs"
                          bg="linear-gradient(135deg, #4299e1 0%, #3182ce 100%)"
                          color="white"
                          onClick={() => handleSubmitReply(comment.id)}
                          borderRadius="md"
                          px={4}
                          py={2}
                          fontSize="xs"
                          fontWeight="semibold"
                          boxShadow="0 2px 8px rgba(66, 153, 225, 0.3)"
                          _hover={{
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(66, 153, 225, 0.4)',
                            bg: 'linear-gradient(135deg, #3182ce 0%, #2c5282 100%)'
                          }}
                          _active={{
                            transform: 'translateY(0)',
                            boxShadow: '0 1px 6px rgba(66, 153, 225, 0.3)'
                          }}
                          transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                        >
                          💭 대댓글 작성
                        </Button>
                      </HStack>
                    </VStack>
                  </Box>
                )}
              </Box>
            ))}
          </VStack>

          {comments.length === 0 && (
            <Center py={8}>
              <Text color="gray.500">
                아직 댓글이 없습니다. 첫 번째 댓글을 작성해보세요!
              </Text>
            </Center>
          )}
        </Box>
      </Box>

      <ImageViewerModal
        isOpen={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        images={viewerImages}
        initialIndex={viewerInitialIndex}
      />
      
      <NicknameModal
        isOpen={nicknameModalOpen}
        onClose={() => setNicknameModalOpen(false)}
        onSuccess={() => {
          // 닉네임 설정 후 사용자 정보 새로고침
          window.location.reload()
        }}
      />
    </DefaultLayout>
  )
}

export default CommunityDetail
